/**
 * Brand: apply an organisation's colour and name to the page.
 *
 * The organisation's brand colour becomes the library palette (--hospora-brand and friends, which the --h-* tokens
 * the app uses are derived from). HGuestLayout sets the same palette on its own element; it is set on <html> as well
 * because the library's teleported overlays (HModal, HDrawer, toasts) render under <body>, outside the layout, and
 * would otherwise fall back to the Hospora default green. Foregrounds are derived by the library so even pale brands
 * stay readable.
 *
 * Typefaces are the Hospora library's (Geist and Bricolage Grotesque) for every organisation: --h-font-body /
 * --h-font-display.
 */

import { buildGuestPalette, paletteToCssVars } from '@hospora/ui/core'

/** The brand of the neutral state (before /config answers, or if it never does): a slate, never an organisation's colour. */
export const NEUTRAL_BRAND = '#1f2937'
const HEX6 = /^#[0-9a-f]{6}$/i

/** A usable 6-digit hex colour, else null (so a malformed colour never reaches CSS). */
export function validBrand(value: string | null | undefined): string | null {
  return typeof value === 'string' && HEX6.test(value) ? value.toLowerCase() : null
}

/** The library palette (--hospora-*) for a brand colour; nothing for a malformed one. */
export function brandTokens(brand: string | null | undefined): Record<string, string> {
  const hex = validBrand(brand)
  return hex ? paletteToCssVars(buildGuestPalette(hex), undefined, { aliases: false }) : {}
}

/** Every custom property brandTokens() can set (read off a sample colour, so the two cannot drift apart). */
export const BRAND_PROPS: readonly string[] = Object.keys(brandTokens('#123456'))

/**
 * Set the palette on the document, and clear any set before (when the organisation changes). A missing or malformed
 * colour gets the neutral palette, matching the brand HGuestLayout is given, so overlays never show the library default.
 */
export function applyBrand(brand: string | null | undefined, root: HTMLElement = document.documentElement): void {
  for (const name of BRAND_PROPS) root.style.removeProperty(name)
  const tokens = validBrand(brand) ? brandTokens(brand) : brandTokens(NEUTRAL_BRAND)
  for (const [name, value] of Object.entries(tokens)) root.style.setProperty(name, value)
}

/**
 * Organisations whose square "mark" (the picture that spins in the payment overlay) is bundled with the app, as
 * it always was for JB & Sons: its hub logo is the full wordmark, which does not make a spinner. Loaded on
 * demand and only for those slugs, so no other organisation's page ever requests it.
 */
export const BRAND_MARK_SLUGS: ReadonlySet<string> = new Set(['jb-and-sons'])
export const brandMarks = { load: async (): Promise<string> => (await import('../assets/images/logo-head.png')).default }

/** Mark the page as belonging to an organisation (data-org on <html>). */
export async function applyOrgIdentity(slug: string | null | undefined, root: HTMLElement = document.documentElement): Promise<void> {
  if (slug) root.setAttribute('data-org', slug)
  else root.removeAttribute('data-org')
}

// A returning visitor's page starts in the colours it last had, rather than flashing the
// neutral stylesheet defaults until the configuration has loaded.
import { apiBaseUrl, sharedOrgSlug, storageKey } from './runtime'
const identityKey = (slug: string) => storageKey(`bookings:org-identity:v2:${slug}:${apiBaseUrl()}`)

export function rememberIdentity(slug: string | null | undefined, brand: string | null | undefined): void {
  try {
    if (slug) localStorage.setItem(identityKey(slug), JSON.stringify({ slug, brand: validBrand(brand) }))
  } catch {
    /* private mode / full: the page still works */
  }
}

/** Apply what was remembered from the last visit (nothing on a first visit). Call before the app mounts. */
export function applyRememberedIdentity(root: HTMLElement = document.documentElement): void {
  applyBrand(null, root)
  void applyOrgIdentity(null, root)
  try {
    // A dedicated host may have been rebound since the last visit. Without a known org, wait for /config.
    const slug = sharedOrgSlug() || import.meta.env.VITE_HOSPORA_ORG
    if (!slug) return
    const raw = localStorage.getItem(identityKey(slug))
    const saved = raw ? (JSON.parse(raw) as { slug?: string; brand?: string | null }) : null
    if (!saved || saved.slug !== slug) return
    applyBrand(saved.brand, root)
    void applyOrgIdentity(saved.slug, root)
  } catch {
    /* nothing remembered, or storage unavailable */
  }
}

/** Title and description from the organisation's name (and its venue names, when it has a few). */
export function applyDocumentMeta(orgName: string | null | undefined, venueNames: string[] = []): void {
  const name = (orgName ?? '').trim()
  if (!name) return
  document.title = `${name} — Bookings`
  const description = venueNames.length > 1
    ? `Book a table at ${name}: ${venueNames.join(', ')}.`
    : `Book a table at ${name}.`
  let meta = document.querySelector('meta[name="description"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute('name', 'description')
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', description)
}
